/**
 * ReportingSectionsQuality — the "Universe quality", "Evidence quality", and
 * "Trail completeness" sections of the Reporting tab: submission-attempt
 * distribution with the IDS note, the IDS-by-quarter trend, the grounded /
 * verified / override meters, and the per-audit trail checklist.
 */
import React from 'react';
import { Section, MicroLabel, useAnalytics, fmtPct, fmtNum } from './ReportingShared.jsx';
import { BucketBars, Sparkline, MeterBar } from './ReportingCharts.jsx';
import { STAGE_LABELS, TRAIL_FLAG_LABELS, UNIVERSE_ATTEMPT_LABELS } from './labels.js';

const ATTEMPT_ORDER = ['0', '1', '2', '3+'];
const ATTEMPT_COLORS = { 0: '#cbd5e1', 1: '#10b981', 2: '#fbbf24', '3+': '#ef4444' };

export function UniverseSection({ qs }) {
  const { loading, error, data } = useAnalytics('universe-quality', qs);
  const u = data?.universe_quality;
  const attempts = u?.universe_attempts || [];
  const idsTrend = u?.ids_findings_by_quarter || [];
  const has3plus = attempts.some(a => a.attempts === '3+' && Number(a.n) > 0);
  return (
    <Section
      title="Universe quality"
      explainer="How many tries it took auditees to hand over a clean data universe, and where invalid data submissions cluster over time."
      loading={loading} error={error} empty={!attempts.length && !idsTrend.length}
      emptyText="No universe submissions recorded in this period yet."
    >
      {attempts.length > 0 && (
        <div>
          <MicroLabel>Submission attempts per audit</MicroLabel>
          <BucketBars items={ATTEMPT_ORDER
            .map(k => ({ key: k, n: attempts.find(a => a.attempts === k)?.n }))
            .filter(a => a.n !== undefined)
            .map(a => ({ label: a.key === '3+' ? '3 or more' : `${a.key} attempt${a.key === '1' ? '' : 's'}`, sublabel: UNIVERSE_ATTEMPT_LABELS[a.key], value: a.n, color: ATTEMPT_COLORS[a.key] }))} height={130} />
          {has3plus && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 mt-2">
              Three failed submissions means the universe could not be tested, which CMS cites as an Invalid Data Submission (IDS).
            </div>
          )}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-3">
        {u?.avg_error_rate !== null && u?.avg_error_rate !== undefined
          ? <>Average universe error rate <b>{u.avg_error_rate}%</b> across {fmtNum(u.audits_with_error_rate)} audit{u.audits_with_error_rate === 1 ? '' : 's'} with a data check.</>
          : 'No universe error rates recorded yet, they appear once data-integrity checks run.'}
      </div>
      {idsTrend.length > 0 && (
        <div className="mt-3">
          <MicroLabel>IDS findings by quarter</MicroLabel>
          <div className="flex items-end gap-4 flex-wrap">
            <Sparkline points={idsTrend.map(r => r.n)} stroke="#ef4444" />
            <div className="flex gap-3">
              {idsTrend.map(r => (
                <div key={r.quarter} className="text-center">
                  <div className="text-sm font-bold text-slate-800 tabular-nums">{r.n}</div>
                  <div className="text-[10px] text-slate-400">{r.quarter}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

export function EvidenceSection({ qs }) {
  const { loading, error, data } = useAnalytics('evidence-quality', qs);
  const e = data?.evidence_quality;
  const o = e?.overall;
  return (
    <Section
      title="Evidence quality"
      explainer="Share of conclusions anchored to verbatim evidence, the number an auditor defends."
      loading={loading} error={error} empty={!o || !o.results_total}
      emptyText="No completed audits in this period yet. Evidence quality is measured on finished fieldwork."
    >
      <div className="grid md:grid-cols-3 gap-4">
        <MeterBar label="Grounded" rate={o?.grounded_rate} barClass="bg-emerald-500"
          sub={`${fmtNum(o?.grounded)} of ${fmtNum(o?.met_or_deficient)} met or deficient calls carry a verbatim quote`} />
        <MeterBar label="Verified" rate={o?.verified_rate} barClass="bg-emerald-500"
          sub={`${fmtNum(o?.verified)} of ${fmtNum(o?.results_total)} conclusions passed the self-check`} />
        <MeterBar label="Human overrides" rate={o?.override_rate} barClass="bg-amber-400"
          sub={`${fmtNum(o?.overridden)} conclusions changed by a reviewer`} />
      </div>
      <div className="text-[11px] text-slate-400 mt-3">
        {fmtNum(o?.results_total)} conclusions across {fmtNum((e?.audits || []).length)} completed audit{(e?.audits || []).length === 1 ? '' : 's'}
        {Number(o?.needs_human_pending) > 0 && <span className="text-amber-600">, {o.needs_human_pending} still waiting on a reviewer ({fmtPct(o.needs_human_cleared_rate)} of flags cleared)</span>}.
      </div>
    </Section>
  );
}

export function TrailSection({ qs }) {
  const { loading, error, data } = useAnalytics('trail-completeness', qs);
  const t = data?.trail || data; // route wraps as { success, trail }
  const audits = t?.audits || [];
  return (
    <Section
      title="Trail completeness"
      explainer="Whether each audit's record would stand on its own in front of a regulator: letter, universe check, conferences, reports, classifications, CAPs, correspondence."
      loading={loading} error={error} empty={!audits.length}
      emptyText="No audits in this period to score."
    >
      <div className="max-w-sm">
        <MeterBar label="Organization average" rate={t?.org_average} barClass="bg-emerald-500"
          sub={`${fmtNum(t?.audits_counted)} audits counted · closed audits average ${fmtPct(t?.closed_audits_average)}`} />
      </div>
      <div className="mt-3 space-y-1.5">
        {audits.map(a => (
          <div key={a.audit_id} className="border border-slate-100 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-800">{a.subject_name}</span>
              <span className="text-[10px] text-slate-400">{STAGE_LABELS[a.stage] || a.stage}</span>
              <span className="ml-auto text-xs font-bold text-slate-700 tabular-nums">{fmtPct(a.score)}</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {Object.entries(a.flags || {}).map(([k, ok]) => (
                <span key={k} className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-slate-400 bg-slate-50 border-slate-200'}`}>
                  <span aria-hidden="true">{ok ? '✓' : '✕'}</span>{TRAIL_FLAG_LABELS[k] || k}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
